import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LoginRequest, Permission, SessionUser, TokenResponse } from '@lumina/types';
import { hasPermission } from '@lumina/types';
import { api, refreshAccessToken } from '../api/client';
import { applyTokens, clearTokens, getRefreshToken, installAuthHandlers, REFRESH_KEY, subscribeAuth } from './tokens';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  isStaff: boolean;
  login: (credentials: LoginRequest) => Promise<SessionUser>;
  logout: () => Promise<void>;
  /** Re-read the signed-in user from the server (after editing your own profile). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session lifecycle: restores the session from the refresh token on load, signs in/out, and reacts to
 * the client's `signed-out` event (refresh failed) or another tab clearing the refresh token.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ status: AuthStatus; user: SessionUser | null }>({ status: 'loading', user: null });

  useState(() => {
    installAuthHandlers();
    return null;
  });

  const signedOut = useCallback(() => {
    clearTokens();
    setState({ status: 'anonymous', user: null });
    // Never show the previous person's cached data to the next one on this browser.
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    return subscribeAuth((event) => {
      if (event.type === 'refreshed') {
        setState({ status: 'authenticated', user: event.user });
      } else {
        signedOut();
      }
    });
  }, [signedOut]);

  // Restore on load. Single-flight in the client means React StrictMode's double mount spends the token once.
  useEffect(() => {
    let cancelled = false;
    if (!getRefreshToken()) {
      setState({ status: 'anonymous', user: null });
      return;
    }
    refreshAccessToken()
      .then((token) => {
        if (!cancelled && token === null) {
          setState({ status: 'anonymous', user: null });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: 'anonymous', user: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === REFRESH_KEY && event.newValue === null) {
        signedOut();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [signedOut]);

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await api.post<TokenResponse>('auth/login', credentials, { anonymous: true });
    applyTokens(response);
    setState({ status: 'authenticated', user: response.user });
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      await api.post('auth/logout', { refreshToken });
    } catch {
      // Signing out locally must always work, even offline or with an expired session.
    }
    signedOut();
  }, [signedOut]);

  const refreshUser = useCallback(async () => {
    const user = await api.get<SessionUser>('auth/me');
    setState({ status: 'authenticated', user });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status: state.status, user: state.user, isStaff: Boolean(state.user?.isStaff), login, logout, refreshUser }),
    [state, login, logout, refreshUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

/** For screens that only render when signed in (everything behind a guard). */
export function useUser(): SessionUser {
  const { user } = useAuth();
  if (!user) {
    throw new Error('useUser called without a signed-in user');
  }
  return user;
}

export interface PermissionApi {
  /** Does the user hold this permission (superlibrarian implies all)? */
  has: (permission: Permission) => boolean;
  hasAny: (permissions: readonly Permission[]) => boolean;
  hasAll: (permissions: readonly Permission[]) => boolean;
  granted: readonly Permission[];
}

export function permissionApi(granted: readonly Permission[]): PermissionApi {
  return {
    granted,
    has: (p) => hasPermission(granted, p),
    hasAny: (list) => list.length === 0 || list.some((p) => hasPermission(granted, p)),
    hasAll: (list) => list.every((p) => hasPermission(granted, p)),
  };
}

export function usePermissions(): PermissionApi {
  const { user } = useAuth();
  return useMemo(() => permissionApi(user?.permissions ?? []), [user]);
}

/** `usePermission('circulate')` or `usePermission(['borrowers', 'reserveforothers'])` (any of). */
export function usePermission(permission: Permission | readonly Permission[]): boolean {
  const api = usePermissions();
  return Array.isArray(permission) ? api.hasAny(permission as readonly Permission[]) : api.has(permission as Permission);
}
